import './Card.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useState, useContext } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useCategory } from '../../CategoryContext';
import { Context } from '../../AuthContext';
import FormDialog from '../FormDialog';
import LinkFilesSection from './LinkFilesSection';
import { useDeleteContent } from '../../pages/DetailViews/useDeleteContent';
import AlertDialog from '../AlertDialog';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import { SuccessToast, ErrorToast } from '../Toast/toast';

const trimVerticalName = (name = '') => (name.length > 35 ? `${name.slice(0, 35)}...` : name);

const copyHeaderStyles = {
  textAlign: 'left',
  color: '#000',
  fontWeight: 700,
};

const Card = ({
  category,
  details,
  editor = false,
  cardHeader,
  icons,
  urlIcons,
  dotColor,
  vertical_id,
  depth = 0,
  sublist = false,
  animation = false,
  handleBookmarkToggle,
  visibleBookmark = true,
  uid = '',
  verticalName = '',
  openNewTab = false,
  onDeleteSuccess = () => {},
  onVisibilityChange = () => {},
}) => {
  const { handleDelete } = useDeleteContent();
  const { updateCount, verticals } = useCategory();
  const { user } = useContext(Context);
  const [note, setNote] = useState(details.note || '');
  const [linkedFiles, setLinkedFiles] = useState(details.linked_files || []);
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);

  // Verticals the user can copy into: only those they can edit (Creator, or Joined
  // with Editor/AdvancedEditor permission), excluding the current vertical. Grouped
  // the same way as the sidebar VerticalSelector (Private / My / Joined).
  const editableVerticals = (verticals || []).filter(
    (v) =>
      v.id !== vertical_id &&
      (v.status === 'Created' ||
        (v.status === 'Joined' && /^(editor|advancededitor)$/i.test(v.permission || '')))
  );
  const draftVerticals = editableVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) === 0
  );
  const myVerticals = editableVerticals.filter(
    (v) => v.status === 'Created' && (v.invited_count || 0) > 0
  );
  const joinedVerticals = editableVerticals.filter((v) => v.status === 'Joined');

  function goTo(path) {
    if (openNewTab) {
      const url = path.startsWith('http') ? path : `${window.location.origin}${path}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  }

  const handleNoteChange = (newNote) => {
    setNote(newNote);
  };

  const handleLinkedFilesChange = (newFiles) => {
    setLinkedFiles(newFiles);
  };
  let clickable = true;
  let type;
  if (cardHeader === 'PUBLICATION' || cardHeader === 'PATENT') {
    const possibleKeys = ['authors', 'inventors'];
    for (let key of possibleKeys) {
      if (key in details) {
        type = key;
        break;
      }
    }
  }

  let cardHeader_ = cardHeader;
  if (cardHeader === 'GRANT') {
    details.title = details.OpportunityTitle;

    if (details.AwardCeiling) {
      details.award_value = details.AwardCeiling;
    } else if (details.AwardFloor) {
      details.award_value = details.AwardFloor;
    }

    cardHeader_ = details?.grant_types?.toUpperCase();
  }

  if (category === 'people') {
    if (details?.type) {
      cardHeader_ = details.type.toUpperCase();
    }
  }

  const [show, setShow] = useState(() => {
    if (['CREATOR', 'EDITOR', 'MEMBER'].includes(cardHeader_)) {
      const matchedVertical = details.verticals?.find((v) => v.id === vertical_id);
      return matchedVertical?.show ?? true;
    } else {
      return details.show ?? false;
    }
  });

  const navigate = useNavigate();

  let urlPrefix = '/vertical/' + vertical_id + '/';

  if (
    depth === 1 &&
    ((cardHeader === 'PUBLICATION' && !details?.openAccessPdf?.url && !details?.url) ||
      (cardHeader === 'PATENT' && !details?.patent_id))
  ) {
    clickable = false;
  }

  function openDetailView() {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      // User is selecting text; don't treat this as a click
      return;
    }

    if (depth === 0) {
      if (cardHeader_ === 'AUTHOR') {
        goTo(`${urlPrefix}author/${details.authorId}`);
      } else if (cardHeader_ === 'INVENTOR') {
        goTo(`${urlPrefix}inventor/${details.name}`);
      } else if (cardHeader_ === 'PUBLICATION') {
        goTo(`${urlPrefix}papers/${details.paperId}`);
      } else if (cardHeader_ === 'PATENT') {
        goTo(`${urlPrefix}patents/${details.publication_number}`);
      } else if (['CREATOR', 'EDITOR', 'MEMBER'].includes(cardHeader_)) {
        goTo(`${urlPrefix}users/${details._id}`);
      } else {
        goTo(`${urlPrefix}${category}/${details._id}`);
      }
    } else {
      if (cardHeader_ === 'AUTHOR') {
        goTo(`${urlPrefix}author/${details.authorId}`);
      } else if (cardHeader_ === 'INVENTOR') {
        goTo(`${urlPrefix}inventor/${details.name}`);
      } else if (cardHeader_ === 'PUBLICATION') {
        if (details?.openAccessPdf?.url) {
          let url = details?.openAccessPdf?.url;
          let external_url = url.startsWith('http') ? url : `https://${url}`;
          window.open(external_url, '_blank');
        } else if (details?.url) {
          window.open(details.url, '_blank');
        }
      } else if (cardHeader_ === 'PATENT') {
        if (details?.patent_id) {
          window.open(`https://patents.google.com/${details.patent_id}`, '_blank');
        }
      }
    }
  }

  function editContent(event) {
    event.stopPropagation();
    const currentUrl = window.location.pathname;

    if (currentUrl.includes('/bookmarks')) {
      switch (category) {
        case 'papers':
          goTo(`${urlPrefix}publications/edit/${details.paperId}`);
          break;
        case 'patents':
          goTo(`${urlPrefix}patents/edit/${details.publication_number}`);
          break;
        case 'inventors':
          goTo(`${urlPrefix}people/edit/inventor/${details.name}`);
          break;
        case 'authors':
          goTo(`${urlPrefix}people/edit/author/${details.authorId}`);
          break;
        case 'grants':
          goTo(`${urlPrefix}funding/edit/${details._id}`);
          break;
        case 'users':
          goTo(`${urlPrefix}people/edit/users/${details._id}`);
          break;
        default:
          goTo(`${urlPrefix}${category}/edit/${details._id}`);
          break;
      }
    } else {
      if (cardHeader_ === 'AUTHOR') {
        goTo('edit/author/' + details.authorId);
      } else if (cardHeader_ === 'INVENTOR') {
        goTo('edit/inventor/' + details.name);
      } else if (cardHeader_ === 'PUBLICATION') {
        goTo('edit/' + details.paperId);
      } else if (cardHeader_ === 'PATENT') {
        goTo('edit/' + details.publication_number);
      } else if (['CREATOR', 'EDITOR', 'MEMBER'].includes(cardHeader_)) {
        goTo('edit/users/' + details._id);
      } else {
        goTo('edit/' + details._id);
      }
    }
  }

  const hideContent = async (event) => {
    event.stopPropagation();
    if (!user) return;

    let category_ = category;
    if (cardHeader_ === 'AUTHOR') {
      category_ = 'authors';
    } else if (cardHeader_ === 'INVENTOR') {
      category_ = 'inventors';
    } else if (cardHeader_ === 'CREATOR' || cardHeader_ === 'EDITOR' || cardHeader_ === 'MEMBER') {
      category_ = 'users';
    }

    const isChecked = !show;
    setShow(isChecked);
    // Keep the parent list's item in sync so visibility-based sorting reads the
    // fresh value without needing a refresh.
    onVisibilityChange(details._id, isChecked);

    if (!show) {
      updateCount(category, 1);
    } else {
      updateCount(category, -1);
    }
    const token = await user.getIdToken();

    const res = await fetch(
      process.env.REACT_APP_FLASK_WEBSERVER +
        'change_visibility?id=' +
        details._id +
        '&collection=' +
        category_ +
        '&vertical_id=' +
        vertical_id,
      {
        method: 'PATCH',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ show: isChecked }),
      }
    );
    await res.json();
  };

  function headingForDelete() {
    if (['papers', 'patents', 'grants', 'organizations', 'jobs', 'events'].includes(category)) {
      return category.slice(0, -1);
    } else if (category === 'people') {
      return details?.type;
    }
  }

  let parent_id = '';
  let otherIds = [];
  let collection_name = category;
  if (category === 'papers') {
    parent_id = details?.paperId;
    otherIds = details?.authors?.map((author) => author.authorId) ?? [];
  } else if (category === 'patents') {
    parent_id = details?.publication_number;
    otherIds = details?.inventors?.map((inventor) => inventor.name) ?? [];
  } else if (category === 'people') {
    if (details?.type === 'author' || details?.type === 'inventor') {
      collection_name = details?.type + 's';
    } else {
      collection_name = 'people';
    }
  }

  const handleCopy = async (event, targetVerticalId) => {
    event.stopPropagation();
    setCopyAnchorEl(null);
    if (!user) return;

    const toastCategory = headingForDelete() || category;
    try {
      const token = await user.getIdToken();
      const res = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'copy_content', {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: details._id,
          collection: collection_name,
          source_vertical_id: vertical_id,
          target_vertical_id: targetVerticalId,
        }),
      });

      if (res.ok) {
        SuccessToast({ category: toastCategory, functionality: 'copy' });
      } else if (res.status === 409) {
        ErrorToast({ category: toastCategory, response: 400 });
      } else {
        ErrorToast({ category: toastCategory, functionality: 'copy' });
      }
    } catch (err) {
      ErrorToast({ category: toastCategory, functionality: 'copy' });
    }
  };

  return (
    <div
      className={`${clickable ? 'card' : 'card-nonclickable'} ${animation ? 'border-pulse' : ''}`}
      onClick={() => openDetailView()}
    >
      <div className="header" style={{ margin: 0, padding: 0 }}>
        <div>
          <span className="dot" style={{ backgroundColor: dotColor }}></span>
          <span className="header-text">
            {cardHeader_} {verticalName && <span className="vertical-name">- {verticalName}</span>}
          </span>
        </div>
        {
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {depth === 0 &&
              !sublist &&
              category !== 'tutorials' &&
              !['MEMBER', 'EDITOR', 'CREATOR'].includes(cardHeader_) && (
                <>
                  <Tooltip title="Copy to another vertical">
                    <ContentCopyIcon
                      style={{ color: '#356AC3' }}
                      className="detail-icon clickable"
                      onClick={(event) => {
                        event.stopPropagation();
                        setCopyAnchorEl(event.currentTarget);
                      }}
                      aria-label="Copy to another vertical"
                    />
                  </Tooltip>
                  <Menu
                    anchorEl={copyAnchorEl}
                    open={Boolean(copyAnchorEl)}
                    onClose={(event) => {
                      event?.stopPropagation?.();
                      setCopyAnchorEl(null);
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {editableVerticals.length === 0 && (
                      <MenuItem disabled>No editable verticals</MenuItem>
                    )}
                    {draftVerticals.length > 0 && (
                      <ListSubheader sx={copyHeaderStyles}>Private Verticals</ListSubheader>
                    )}
                    {draftVerticals.map((v) => (
                      <MenuItem key={v.id} onClick={(event) => handleCopy(event, v.id)}>
                        {trimVerticalName(v.name)}
                      </MenuItem>
                    ))}
                    {myVerticals.length > 0 && (
                      <ListSubheader sx={copyHeaderStyles}>My Verticals</ListSubheader>
                    )}
                    {myVerticals.map((v) => (
                      <MenuItem key={v.id} onClick={(event) => handleCopy(event, v.id)}>
                        {trimVerticalName(v.name)}
                      </MenuItem>
                    ))}
                    {joinedVerticals.length > 0 && (
                      <ListSubheader sx={copyHeaderStyles}>Joined Verticals</ListSubheader>
                    )}
                    {joinedVerticals.map((v) => (
                      <MenuItem key={v.id} onClick={(event) => handleCopy(event, v.id)}>
                        {trimVerticalName(v.name)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            {editor && depth === 0 && !sublist && category !== 'tutorials' && (
              <>
                {show ? (
                  <Tooltip title="Visible to Members">
                    <VisibilityOutlinedIcon
                      style={{ color: '#356AC3' }}
                      className="detail-icon clickable"
                      onClick={(event) => hideContent(event)}
                      aria-label="Content is visible"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Hidden To Members">
                    <VisibilityOffOutlinedIcon
                      style={{ color: '#356AC3' }}
                      className="detail-icon clickable"
                      onClick={(event) => hideContent(event)}
                      aria-label="Content is hidden"
                    />
                  </Tooltip>
                )}
                <FontAwesomeIcon
                  color="#356AC3"
                  icon={faPenToSquare}
                  className="detail-icon clickable"
                  onClick={(event) => editContent(event)}
                />
                {!['MEMBER', 'EDITOR', 'CREATOR'].includes(cardHeader_) && (
                  <AlertDialog
                    id={details._id}
                    action={async () => {
                      await handleDelete({
                        id: details._id,
                        collection: collection_name,
                        verticalId: vertical_id,
                        parent_id: parent_id,
                        otherIds: otherIds,
                        bookmarked: details?.isBookmarked,
                      });
                      onDeleteSuccess(details._id);
                    }}
                    index={-1}
                    dialogTitle={`Delete ${headingForDelete()}: ${
                      details?.name ? details?.name : details?.title && details.title
                    }`}
                    dialogContentText={`This action cannot be undone. This will permanently delete the ${headingForDelete()} and remove the data from our servers.`}
                    color="blue"
                  />
                )}
              </>
            )}

            {depth === 0 && visibleBookmark && (
              <>
                {details?.isBookmarked ? (
                  <BookmarkIcon
                    onClick={handleBookmarkToggle}
                    style={{ color: '#356AC3' }}
                    className="detail-icon clickable"
                  />
                ) : (
                  <BookmarkBorderIcon
                    onClick={handleBookmarkToggle}
                    style={{ color: '#356AC3' }}
                    className="detail-icon clickable"
                  />
                )}
              </>
            )}
          </div>
        }
      </div>

      <div>
        {category !== 'papers' && category !== 'patents' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              margin:
                category === 'people' ||
                category === 'authors' ||
                category === 'inventors' ||
                category === 'organizations' ||
                details?.image_url
                  ? '20px 0 20px 0'
                  : 0,
            }}
          >
            {category === 'people' ||
            category === 'authors' ||
            category === 'inventors' ||
            category === 'users' ? (
              details.image_url ? (
                <img
                  src={details.image_url}
                  alt="Profile"
                  style={{
                    height: '64px',
                    width: '64px',
                    marginRight: '20px',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                <AccountCircleOutlinedIcon
                  style={{ height: '64px', width: '64px', paddingRight: '20px' }}
                />
              )
            ) : category === 'organizations' ? (
              details.image_url ? (
                <img
                  src={details.image_url}
                  alt="Organization"
                  style={{ height: '64px', width: '64px', marginRight: '20px', objectFit: 'cover' }}
                />
              ) : (
                <AccountBalanceOutlinedIcon
                  style={{ height: '64px', width: '64px', paddingRight: '20px' }}
                />
              )
            ) : (
              (category === 'grants' || category === 'jobs' || category === 'events') &&
              details?.image_url && (
                <img
                  src={details.image_url}
                  alt="Organization"
                  style={{ height: '64px', width: '64px', marginRight: '20px', objectFit: 'cover' }}
                />
              )
            )}

            <div>
              {details?.name ? (
                <h2 className="title">{details.name}</h2>
              ) : (
                details?.title && <h2 className="title">{details.title}</h2>
              )}
            </div>
          </div>
        ) : (
          details?.title && <h2 className="title">{details.title}</h2>
        )}

        <div>
          {details?.inventors?.length > 0 && (
            <div className="detail-item" style={{ color: 'green' }}>
              {icons[type]}
              <div className="people">
                {details.inventors.map((inventor, index) => (
                  <div key={inventor.name}>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        goTo(`${urlPrefix}inventor/${inventor.name}`);
                      }}
                      className="person-link"
                    >
                      {inventor.name}
                    </span>
                    {index < details.inventors.length - 1 && <span>,&nbsp;</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {details?.authors?.length > 0 && (
            <div className="detail-item" style={{ color: 'green' }}>
              {icons[type]}
              <div className="people">
                {details.authors.map((author, index) => (
                  <div key={author.authorId}>
                    {depth === 0 ? (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          goTo(`${urlPrefix}author/${author.authorId}`);
                        }}
                        className="person-link"
                      >
                        {author.name}
                      </span>
                    ) : author?.external_link === '' ? (
                      <span onClick={(e) => e.stopPropagation()}>{author.name}</span>
                    ) : author?.external_link ? (
                      <a
                        href={
                          author.external_link.startsWith('http')
                            ? author.external_link
                            : `https://${author.external_link}`
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span onClick={(e) => e.stopPropagation()}>{author.name}</span>
                      </a>
                    ) : (
                      <a
                        href={`https://www.semanticscholar.org/author/${author.authorId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span onClick={(e) => e.stopPropagation()}>{author.name}</span>
                      </a>
                    )}
                    {index < details.authors.length - 1 && <span>,&nbsp;</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {depth === 0 && (note || editor || linkedFiles.length > 0) && (
            <div
              style={{
                width: '48%',
                float: 'right',
                marginLeft: '12px',
                marginBottom: '12px',
              }}
            >
              {(note || editor) && (
                <div
                  style={{
                    border: '1px solid #DDDEDD',
                    borderRadius: '4px',
                    backgroundColor: '#F9F9FC',
                    paddingLeft: '15px',
                    paddingRight: '15px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <p
                      style={{
                        fontWeight: '600',
                        lineHeight: '20px',
                        marginTop: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      📝 Editor’s Note:
                    </p>
                    {editor && (
                      <FormDialog
                        title="Note"
                        description="content item"
                        content={note}
                        onChangeText={handleNoteChange}
                        _id={details._id}
                        category={category}
                        cardHeader={cardHeader_}
                        verticalId={vertical_id}
                      />
                    )}
                  </div>
                  {note && (
                    <p style={{ fontWeight: '400', fontSize: '14px', marginTop: '0' }}>{note}</p>
                  )}
                </div>
              )}
              {(editor || linkedFiles.length > 0) && (
                <LinkFilesSection
                  category={category}
                  cardHeader={cardHeader_}
                  _id={details._id}
                  verticalId={vertical_id}
                  files={linkedFiles}
                  canEdit={editor}
                  onChange={handleLinkedFilesChange}
                />
              )}
            </div>
          )}

          <div>
            {Object.entries(details).map(
              ([key, value]) =>
                key in icons &&
                key !== 'authors' &&
                key !== 'inventors' &&
                value && (
                  <div key={key} className="detail-item">
                    {icons[key]}
                    {value}
                  </div>
                )
            )}

            {details?.externalIds?.DOI && (
              <div className="detail-item">
                <img src={`${process.env.PUBLIC_URL}/doi.svg`} alt="DOI" className="detail-icon" />
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`https://doi.org/${details.externalIds.DOI}`}
                  onClick={(event) => {
                    event.stopPropagation(); // Prevent click from propagating to the detail view
                  }}
                >
                  {details.externalIds.DOI}
                </a>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {urlIcons &&
                Object.entries(details).map(
                  ([key, value]) =>
                    key in urlIcons &&
                    value && (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={
                          value.startsWith('http://') || value.startsWith('https://')
                            ? value
                            : `https://${value}`
                        }
                        onClick={(event) => {
                          event.stopPropagation(); // Prevent click from propagating to the detail view
                        }}
                      >
                        {urlIcons[key]}
                      </a>
                    )
                )}
            </div>

            {details?.updated_at && (
              <span className="footer">Last Updated On: {details.updated_at}</span>
            )}
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      </div>
    </div>
  );
};

export default Card;
